<template>
  <div class="dashboard-premium">
    <b-row class="mb-2">
      <b-col cols="12" class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h2 class="dashboard-title mb-50">Flare Analytics Dashboard</h2>
          <p class="dashboard-subtitle mb-0">Executive overview of revenue, orders, funnel and customer activity</p>
        </div>

        <div class="d-flex align-items-center mt-1 mt-md-0">
          <b-button-group>
            <b-button
              v-for="r in ranges"
              :key="r.value"
              :variant="selectedRange === r.value ? 'primary' : 'outline-primary'"
              @click="changeRange(r.value)"
            >
              {{ r.label }}
            </b-button>
          </b-button-group>
        </div>
      </b-col>
    </b-row>

    <div v-if="loading" class="loading-wrap">
      <b-card class="premium-card">
        <div class="text-center py-3">Loading dashboard...</div>
      </b-card>
    </div>

    <template v-else>
      <!-- Core KPI -->
      <b-row>
        <b-col xl="3" md="6" sm="6" v-for="card in statCards" :key="card.key">
          <b-card class="premium-card stat-card mb-2">
            <div>
              <div class="stat-label">{{ card.label }}</div>
              <div class="stat-value">{{ card.value }}</div>
              <div class="stat-meta">{{ card.meta }}</div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Funnel KPI -->
      <b-row>
        <b-col xl="2" lg="4" md="6" sm="6">
          <b-card class="premium-card stat-card mb-2 live-card">
            <div>
              <div class="stat-label">Live Visitors</div>
              <div class="stat-value">{{ number(liveVisitors.live_visitors || 0) }}</div>
              <div class="stat-meta">Active in last 2 minutes</div>
            </div>
          </b-card>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="6">
          <b-card class="premium-card stat-card mb-2">
            <div>
              <div class="stat-label">Product Views</div>
              <div class="stat-value">{{ number(funnelSummary.product_views || 0) }}</div>
              <div class="stat-meta">{{ funnelSummary.view_to_cart_rate || 0 }}% to cart</div>
            </div>
          </b-card>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="6">
          <b-card class="premium-card stat-card mb-2">
            <div>
              <div class="stat-label">Add To Cart</div>
              <div class="stat-value">{{ number(funnelSummary.add_to_cart || 0) }}</div>
              <div class="stat-meta">{{ funnelSummary.cart_to_checkout_rate || 0 }}% to checkout</div>
            </div>
          </b-card>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="6">
          <b-card class="premium-card stat-card mb-2">
            <div>
              <div class="stat-label">Checkout Starts</div>
              <div class="stat-value">{{ number(funnelSummary.checkout_starts || 0) }}</div>
              <div class="stat-meta">{{ funnelSummary.checkout_to_purchase_rate || 0 }}% to purchase</div>
            </div>
          </b-card>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="6">
          <b-card class="premium-card stat-card mb-2">
            <div>
              <div class="stat-label">Purchases</div>
              <div class="stat-value">{{ number(funnelSummary.purchases || 0) }}</div>
              <div class="stat-meta">Completed orders</div>
            </div>
          </b-card>
        </b-col>

        <b-col xl="2" lg="4" md="6" sm="6">
          <b-card class="premium-card stat-card mb-2 conversion-card">
            <div>
              <div class="stat-label">Conversion Rate</div>
              <div class="stat-value">{{ funnelSummary.overall_conversion_rate || 0 }}%</div>
              <div class="stat-meta">View to purchase</div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Existing Charts -->
      <b-row>
        <b-col xl="8" lg="7">
          <b-card class="premium-card chart-card mb-2">
            <div class="mb-2">
              <h4 class="mb-25">Revenue vs Orders</h4>
              <small class="text-muted">Revenue uses left axis, orders use right axis</small>
            </div>

            <div v-if="revenueChart.length">
              <apexchart
                type="line"
                height="330"
                :options="revenueChartOptions"
                :series="revenueSeries"
              />
            </div>
            <div v-else class="empty-wrap">No chart data found</div>
          </b-card>
        </b-col>

        <b-col xl="4" lg="5">
          <b-card class="premium-card chart-card mb-2">
            <div class="mb-2">
              <h4 class="mb-25">Order Status Breakdown</h4>
              <small class="text-muted">Top statuses grouped for clean view</small>
            </div>

            <div v-if="statusSeries.length">
              <apexchart
                type="donut"
                height="330"
                :options="statusChartOptions"
                :series="statusSeries"
              />
            </div>
            <div v-else class="empty-wrap">No status data found</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Existing tables -->
      <b-row>
        <b-col xl="6">
          <b-card class="premium-card mb-2 table-card">
            <div class="mb-2">
              <h4 class="mb-25">Top Customers</h4>
              <small class="text-muted">Highest spenders by delivered revenue</small>
            </div>

            <div class="table-responsive">
              <table class="table premium-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Orders</th>
                    <th>Spent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in topCustomers" :key="c.id">
                    <td>{{ c.full_name || 'N/A' }}</td>
                    <td>{{ c.phone || 'N/A' }}</td>
                    <td>{{ number(c.total_orders) }}</td>
                    <td>{{ money(c.total_spent) }}</td>
                  </tr>
                  <tr v-if="!topCustomers.length">
                    <td colspan="4" class="text-center text-muted py-3">No customer data found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>

        <b-col xl="6">
          <b-card class="premium-card mb-2 table-card">
            <div class="mb-2">
              <h4 class="mb-25">Recent Orders</h4>
              <small class="text-muted">Latest order flow snapshot</small>
            </div>

            <div class="table-responsive">
              <table class="table premium-table mb-0">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="o in recentOrders" :key="o.id">
                    <td>#{{ o.large_order_id || o.id }}</td>
                    <td>{{ o.full_name || 'N/A' }}</td>
                    <td>
                      <b-badge pill :variant="statusVariant(o.status)">
                        {{ o.status }}
                      </b-badge>
                    </td>
                    <td>{{ money(o.total_price) }}</td>
                  </tr>
                  <tr v-if="!recentOrders.length">
                    <td colspan="4" class="text-center text-muted py-3">No order data found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- New analytics tables -->
      <b-row>
        <b-col xl="6">
          <b-card class="premium-card mb-2 table-card">
            <div class="mb-2">
              <h4 class="mb-25">Top Selling Products</h4>
              <small class="text-muted">Best selling products in selected range</small>
            </div>

            <div class="table-responsive">
              <table class="table premium-table mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Orders</th>
                    <th>Qty</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in topSellingProducts" :key="item.id">
                    <td>
                      <div class="product-cell">
                        <img
                          :src="productImage(item)"
                          alt="Product"
                          class="product-thumb"
                          @error="onImageError"
                        >
                        <div class="product-info">
                          <div class="product-name">{{ cleanProductName(item.name) }}</div>
                        </div>
                      </div>
                    </td>
                    <td>{{ number(item.total_orders) }}</td>
                    <td>{{ number(item.total_qty) }}</td>
                    <td>{{ money(item.revenue) }}</td>
                  </tr>
                  <tr v-if="!topSellingProducts.length">
                    <td colspan="4" class="text-center text-muted py-3">No product sales data found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>

        <b-col xl="6">
          <b-card class="premium-card mb-2 table-card">
            <div class="mb-2">
              <h4 class="mb-25">Low Conversion Products</h4>
              <small class="text-muted">Products with views but weak add-to-cart rate</small>
            </div>

            <div class="table-responsive">
              <table class="table premium-table mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Views</th>
                    <th>Add To Cart</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in lowConversionProducts" :key="item.id">
                    <td>
                      <div class="product-cell">
                        <img
                          :src="productImage(item)"
                          alt="Product"
                          class="product-thumb"
                          @error="onImageError"
                        >
                        <div class="product-info">
                          <div class="product-name">{{ cleanProductName(item.name) }}</div>
                        </div>
                      </div>
                    </td>
                    <td>{{ number(item.product_views) }}</td>
                    <td>{{ number(item.add_to_cart) }}</td>
                    <td>{{ item.add_to_cart_rate }}%</td>
                  </tr>
                  <tr v-if="!lowConversionProducts.length">
                    <td colspan="4" class="text-center text-muted py-3">No conversion data found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Current Page Viewers -->
      <b-row>
        <b-col xl="12">
          <b-card class="premium-card mb-2 table-card">
            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
              <div>
                <h4 class="mb-25">Current Page Viewers</h4>
                <small class="text-muted">Live active visitors by current page</small>
              </div>
              <small class="text-muted">
                Updated {{ liveUpdatedText }}
              </small>
            </div>

            <div class="table-responsive">
              <table class="table premium-table mb-0">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Viewers</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in currentPageViewers" :key="`${item.current_page}-${index}`">
                    <td>{{ item.current_page || '/' }}</td>
                    <td>{{ number(item.viewers) }}</td>
                  </tr>
                  <tr v-if="!currentPageViewers.length">
                    <td colspan="2" class="text-center text-muted py-3">No active page viewers found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Delivery split -->
      <b-row>
        <b-col xl="12">
          <b-card class="premium-card mb-2 table-card">
            <div class="mb-2">
              <h4 class="mb-25">Delivery Split</h4>
              <small class="text-muted">Inside Dhaka / Outside Dhaka ratio, value and delivery charge</small>
            </div>

            <div class="table-responsive">
              <table class="table premium-table mb-0">
                <thead>
                  <tr>
                    <th>Zone</th>
                    <th>Orders</th>
                    <th>Total Amount</th>
                    <th>Delivery Charge</th>
                    <th>Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in deliverySplit" :key="item.zone">
                    <td>{{ item.zone || 'Unknown' }}</td>
                    <td>{{ number(item.total_orders) }}</td>
                    <td>{{ money(item.total_amount) }}</td>
                    <td>{{ money(item.total_delivery_charge) }}</td>
                    <td>{{ item.ratio }}%</td>
                  </tr>
                  <tr v-if="!deliverySplit.length">
                    <td colspan="5" class="text-center text-muted py-3">No delivery split data found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import axios from '@/helpers/axios'
import { BRow, BCol, BCard, BButton, BButtonGroup, BBadge } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60"><rect width="100%" height="100%" fill="%23f3f2f7"/><text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="10">No Image</text></svg>'

export default {
  name: 'AdminDashboardView',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BButtonGroup,
    BBadge,
    apexchart: VueApexCharts,
  },

  data() {
    return {
      loading: true,
      selectedRange: 'today',
      ranges: [
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: '7D', value: '7d' },
        { label: '30D', value: '30d' },
      ],
      overview: {},
      revenueChart: [],
      rawStatusData: {},
      topCustomers: [],
      recentOrders: [],
      liveVisitors: {},
      funnelSummary: {},
      topSellingProducts: [],
      lowConversionProducts: [],
      deliverySplit: [],
      currentPageViewers: [],
      liveRefreshTimer: null,
      lastLiveUpdatedAt: null,
      placeholderImage: PLACEHOLDER_IMAGE,
    }
  },

  computed: {
    statCards() {
      return [
        {
          key: 'today_revenue',
          label: 'Today Sales',
          value: this.money(this.overview.today_revenue || 0),
          meta: `Delivered ${this.money(this.overview.today_delivered_revenue || 0)}`,
        },
        {
          key: 'today_delivered_orders',
          label: 'Today Delivered',
          value: this.number(this.overview.today_delivered_orders || 0),
          meta: 'Delivered order count',
        },
        {
          key: 'today_orders',
          label: 'Orders In Range',
          value: this.number(this.overview.today_orders || 0),
          meta: 'Orders in selected range',
        },
        {
          key: 'customers',
          label: 'Customers',
          value: this.number(this.overview.total_customers || 0),
          meta: 'Total users',
        },
      ]
    },

    revenueSeries() {
      return [
        {
          name: 'Revenue',
          type: 'line',
          data: this.revenueChart.map(i => Number(i.revenue || 0)),
        },
        {
          name: 'Orders',
          type: 'line',
          data: this.revenueChart.map(i => Number(i.orders_count || 0)),
        },
      ]
    },

    revenueChartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: this.revenueChart.length <= 1 ? 'straight' : 'smooth',
          width: [4, 3],
        },
        markers: {
          size: this.revenueChart.length <= 1 ? 6 : 3,
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: this.revenueChart.map(i => i.date),
        },
        legend: {
          position: 'bottom',
        },
        yaxis: [
          {
            title: { text: 'Revenue' },
            labels: {
              formatter: value => this.shortMoney(value),
            },
          },
          {
            opposite: true,
            title: { text: 'Orders' },
            labels: {
              formatter: value => Math.round(value),
            },
          },
        ],
        tooltip: {
          y: [
            {
              formatter: val => this.money(val),
            },
            {
              formatter: val => `${Math.round(val)} orders`,
            },
          ],
        },
        noData: {
          text: 'No chart data found',
        },
      }
    },

    groupedStatusData() {
      const entries = Object.entries(this.rawStatusData || {})
        .map(([key, value]) => ({
          key,
          value: Number(value || 0),
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value)

      const top = entries.slice(0, 5)
      const rest = entries.slice(5)
      const othersTotal = rest.reduce((sum, item) => sum + item.value, 0)

      if (othersTotal > 0) {
        top.push({ key: 'others', value: othersTotal })
      }

      return top
    },

    statusSeries() {
      return this.groupedStatusData.map(item => item.value)
    },

    statusChartOptions() {
      return {
        labels: this.groupedStatusData.map(item => item.key),
        legend: {
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
        },
        tooltip: {
          y: {
            formatter: val => `${this.number(val)} orders`,
          },
        },
      }
    },

    liveUpdatedText() {
      if (!this.lastLiveUpdatedAt) return 'just now'
      return this.lastLiveUpdatedAt.toLocaleTimeString()
    },
  },

  async mounted() {
    await this.load()
    this.startLiveRefresh()
  },

  beforeDestroy() {
    this.stopLiveRefresh()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        await Promise.all([
          this.getOverview(),
          this.getChart(),
          this.getStatus(),
          this.getCustomers(),
          this.getOrders(),
          this.getLiveVisitors(),
          this.getFunnelSummary(),
          this.getTopSellingProducts(),
          this.getLowConversionProducts(),
          this.getDeliverySplit(),
          this.getCurrentPageViewers(),
        ])
      } finally {
        this.loading = false
      }
    },

    async getOverview() {
      const { data } = await axios.get(`/api/v1/dashboard/overview?range=${this.selectedRange}`)
      this.overview = data || {}
    },

    async getChart() {
      const { data } = await axios.get(`/api/v1/dashboard/revenue-chart?range=${this.selectedRange}`)
      this.revenueChart = Array.isArray(data) ? data : []
    },

    async getStatus() {
      const { data } = await axios.get('/api/v1/dashboard/order-status')
      this.rawStatusData = data || {}
    },

    async getCustomers() {
      const { data } = await axios.get('/api/v1/dashboard/top-customers')
      this.topCustomers = Array.isArray(data) ? data : []
    },

    async getOrders() {
      const { data } = await axios.get('/api/v1/dashboard/recent-orders')
      this.recentOrders = Array.isArray(data) ? data : []
    },

    async getLiveVisitors() {
      const { data } = await axios.get('/api/v1/dashboard/live-visitors')
      this.liveVisitors = data || {}
    },

    async getFunnelSummary() {
      const { data } = await axios.get(`/api/v1/dashboard/funnel-summary?range=${this.selectedRange}`)
      this.funnelSummary = data || {}
    },

    async getTopSellingProducts() {
      const { data } = await axios.get(`/api/v1/dashboard/top-selling-products?range=${this.selectedRange}`)
      this.topSellingProducts = Array.isArray(data) ? data : []
    },

    async getLowConversionProducts() {
      const { data } = await axios.get(`/api/v1/dashboard/low-conversion-products?range=${this.selectedRange}`)
      this.lowConversionProducts = Array.isArray(data) ? data : []
    },

    async getDeliverySplit() {
      const { data } = await axios.get(`/api/v1/dashboard/delivery-split?range=${this.selectedRange}`)
      this.deliverySplit = Array.isArray(data) ? data : []
    },

    async getCurrentPageViewers() {
      const { data } = await axios.get('/api/v1/dashboard/current-page-viewers')
      this.currentPageViewers = Array.isArray(data) ? data : []
      this.lastLiveUpdatedAt = new Date()
    },

    startLiveRefresh() {
      this.stopLiveRefresh()
      this.liveRefreshTimer = setInterval(async () => {
        try {
          await Promise.all([
            this.getLiveVisitors(),
            this.getCurrentPageViewers(),
          ])
        } catch (e) {
          console.error('Live refresh failed', e)
        }
      }, 15000)
    },

    stopLiveRefresh() {
      if (this.liveRefreshTimer) {
        clearInterval(this.liveRefreshTimer)
        this.liveRefreshTimer = null
      }
    },

    async changeRange(r) {
      this.selectedRange = r
      this.loading = true
      try {
        await Promise.all([
          this.getOverview(),
          this.getChart(),
          this.getFunnelSummary(),
          this.getTopSellingProducts(),
          this.getLowConversionProducts(),
          this.getDeliverySplit(),
        ])
      } finally {
        this.loading = false
      }
    },

    money(v) {
      return `BDT ${Number(v || 0).toLocaleString()}`
    },

    shortMoney(v) {
      const n = Number(v || 0)
      if (n >= 10000000) return `BDT ${(n / 10000000).toFixed(1)}Cr`
      if (n >= 100000) return `BDT ${(n / 1000).toFixed(0)}K`
      return `BDT ${n.toLocaleString()}`
    },

    number(v) {
      return Number(v || 0).toLocaleString()
    },

    statusVariant(status) {
      const map = {
        pending: 'warning',
        'pending-1': 'warning',
        'pending-2': 'warning',
        processing: 'info',
        packing: 'primary',
        shipping: 'primary',
        on_the_way: 'primary',
        delivered: 'success',
        rejected: 'danger',
        returned: 'secondary',
        canceled: 'danger',
      }
      return map[status] || 'light'
    },

    productImage(item) {
      return item.image_url || this.placeholderImage
    },

    cleanProductName(name) {
      if (!name) return 'N/A'
      return String(name).replace(/[\u{1F300}-\u{1FAFF}]/gu, '').trim()
    },

    onImageError(event) {
      event.target.src = this.placeholderImage
    },
  },
}
</script>

<style scoped>
.dashboard-premium {
  padding: 10px;
}

.dashboard-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: #5e5873;
}

.dashboard-subtitle {
  color: #6e6b7b;
}

.premium-card {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.08);
}

.stat-card {
  min-height: 118px;
}

.stat-label {
  font-size: 0.95rem;
  color: #6e6b7b;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #5e5873;
  line-height: 1.2;
}

.stat-meta {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #b9b9c3;
}

.live-card .stat-value {
  color: #7367f0;
}

.conversion-card .stat-value {
  color: #28c76f;
}

.chart-card {
  min-height: 420px;
}

.table-card {
  min-height: 100%;
}

.premium-table thead th {
  border-top: 0;
  color: #6e6b7b;
  font-size: 0.83rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.premium-table tbody td {
  vertical-align: middle;
  font-size: 0.94rem;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
}

.product-thumb {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #ebe9f1;
  background: #fff;
  flex-shrink: 0;
}

.product-info {
  min-width: 0;
}

.product-name {
  color: #5e5873;
  font-weight: 500;
  line-height: 1.35;
}

.loading-wrap,
.empty-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: #6e6b7b;
}
</style>